import React, { createContext, useContext, useState, useEffect } from 'react';

// Create context
const LanguageContext = createContext();

// Language translations
const translations = {
  en: {
    // App
    appName: 'KisanSetu',
    // Navigation
    home: 'Home',
    navDashboard: 'Dashboard',
    navFields: 'Fields',
    navMoney: 'Money Records',
    navBorrowings: 'Borrowings',
    navLotNumbers: 'Lot Numbers',
    navTransportation: 'Transportation',
    navLabour: 'Labour Management',
    logout: 'Logout',
    login: 'Login',
    signup: 'Sign Up',
    welcome: 'Welcome',
    hi: 'Hi',

    // Common UI Elements
    addMoneyRecord: 'Add Money Record',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    update: 'Update',
    search: 'Search',
    filter: 'Filter',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    warning: 'Warning',
    info: 'Information',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    submit: 'Submit',
    reset: 'Reset',
    clear: 'Clear',
    select: 'Select',
    view: 'View',
    download: 'Download',
    export: 'Export',
    import: 'Import',
    print: 'Print',
    refresh: 'Refresh',
    settings: 'Settings',
    profile: 'Profile',
    admin: 'Admin',
    user: 'User',
    date: 'Date',
    time: 'Time',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    total: 'Total',
    count: 'Count',
    amount: 'Amount',
    quantity: 'Quantity',
    price: 'Price',
    cost: 'Cost',
    revenue: 'Revenue',
    profit: 'Profit',
    loss: 'Loss',
    balance: 'Balance',
    payment: 'Payment',
    receipt: 'Receipt',
    invoice: 'Invoice',
    order: 'Order',
    delivery: 'Delivery',
    shipping: 'Shipping',
    tracking: 'Tracking',
    notes: 'Notes',
    description: 'Description',
    details: 'Details',
    history: 'History',
    log: 'Log',
    report: 'Report',
    analytics: 'Analytics',
    statistics: 'Statistics',
    about: 'About',
    contact: 'Contact',
    help: 'Help',
    support: 'Support',
    faq: 'FAQ',
    terms: 'Terms',
    privacy: 'Privacy',
    legal: 'Legal',
    copyright: 'Copyright',
    version: 'Version',
    updateAvailable: 'Update Available',
    maintenance: 'Maintenance',
    offline: 'Offline',
    online: 'Online',
    connected: 'Connected',
    disconnected: 'Disconnected',
    syncing: 'Syncing',
    synced: 'Synced',
    failed: 'Failed',
    pending: 'Pending',
    completed: 'Completed',
    cancelled: 'Cancelled',
    rejected: 'Rejected',
    approved: 'Approved',
    processing: 'Processing',
    processed: 'Processed',
    queued: 'Queued',
    scheduled: 'Scheduled',
    ongoing: 'Ongoing',
    finished: 'Finished',
    started: 'Started',
    stopped: 'Stopped',
    paused: 'Paused',
    resumed: 'Resumed',

    // Subtitles and Descriptions
    welcomeSubtitle: 'Welcome to your farm management system',
    dashboardSubtitle: 'Monitor your farm performance and activities',
    fieldsSubtitle: 'Manage your agricultural fields and crops',
    moneySubtitle: 'Track income, expenses, and financial records',
    borrowingsSubtitle: 'Manage loans and borrowing records',
    lotNumbersSubtitle: 'Track potato lots and storage information',
    transportationSubtitle: 'Monitor transportation of agricultural products',
    labourSubtitle: 'Manage workers and payroll information',
    settingsSubtitle: 'Configure application preferences',
    profileSubtitle: 'Manage your account and personal information',
    reportsSubtitle: 'Generate and view various reports',
    analyticsSubtitle: 'View detailed analytics and insights',
    notificationsSubtitle: 'Manage system notifications and alerts',
    helpSubtitle: 'Get help and support for using the application',
    aboutSubtitle: 'Learn more about the farm management system',
    contactSubtitle: 'Get in touch with support team',
    loginSubtitle: 'Sign in to access your farm dashboard',
    signupSubtitle: 'Create a new account to get started',
    forgotPasswordSubtitle: 'Reset your password if you forgot it',
    securitySubtitle: 'Manage your account security settings',
    privacySubtitle: 'Control your data and privacy settings',
    backupSubtitle: 'Backup and restore your farm data',
    exportSubtitle: 'Export your data in various formats',
    importSubtitle: 'Import data from external sources',
    syncSubtitle: 'Synchronize data across devices',
    maintenanceSubtitle: 'System maintenance and updates',
    offlineSubtitle: 'Work offline and sync when online',
    mobileSubtitle: 'Access your farm data on mobile devices',
    collaborationSubtitle: 'Work with team members and partners',
    integrationSubtitle: 'Connect with other farm management tools',
    automationSubtitle: 'Automate routine tasks and processes',
    forecastingSubtitle: 'Predict future trends and outcomes',
    planningSubtitle: 'Plan and schedule farm activities',
    monitoringSubtitle: 'Monitor real-time farm conditions',
    alertsSubtitle: 'Receive important alerts and notifications',
    historySubtitle: 'View historical data and trends',
    performanceSubtitle: 'Track performance metrics and KPIs',
    efficiencySubtitle: 'Improve farm efficiency and productivity',
    sustainabilitySubtitle: 'Monitor and improve sustainability practices',
    complianceSubtitle: 'Ensure regulatory compliance',
    documentationSubtitle: 'Maintain proper documentation and records',
    trainingSubtitle: 'Access training materials and guides',
    supportSubtitle: 'Get technical support and assistance',

    // Page Descriptions
    fieldsManagementDescription: 'Manage your agricultural fields, track crop yields, and monitor field performance',
    moneyRecordsDescription: 'Track all income and expenses, manage financial records, and monitor cash flow',
    borrowingsManagementDescription: 'Manage loans, track repayments, and monitor borrowing history',
    lotNumberManagementDescription: 'Track potato lots, manage storage information, and monitor inventory',
    lotNumbersManagementDescription: 'Track potato lots, manage storage information, and monitor inventory',
    transportationManagementDescription: 'Monitor transportation of agricultural products from fields to storage',
    labourManagementDescription: 'Manage workers, track attendance, handle payroll, and monitor labor costs',
    dashboardDescription: 'View comprehensive overview of your farm operations and performance metrics',
    settingsDescription: 'Configure application settings, manage preferences, and customize your experience',
    profileDescription: 'Manage your personal information, account settings, and user preferences',
    reportsDescription: 'Generate detailed reports, analyze trends, and export business insights',
    analyticsDescription: 'View detailed analytics, track performance metrics, and gain business insights',
    notificationsDescription: 'Manage system notifications, set alerts, and stay updated on important events',
    helpDescription: 'Access help documentation, tutorials, and support resources',
    aboutDescription: 'Learn about our farm management system, features, and company information',
    contactDescription: 'Get in touch with our support team, provide feedback, or request assistance',
    loginDescription: 'Sign in to your account to access your farm management dashboard',
    signupDescription: 'Create a new account to start managing your farm with our comprehensive tools',
    forgotPasswordDescription: 'Reset your password securely and regain access to your account',
    securityDescription: 'Manage account security, enable two-factor authentication, and protect your data',
    privacyDescription: 'Control your privacy settings, manage data sharing, and protect your information',
    backupDescription: 'Backup your farm data, restore previous versions, and ensure data safety',
    exportDescription: 'Export your farm data in various formats for analysis and record-keeping',
    importDescription: 'Import data from external sources, migrate information, and streamline setup',
    syncDescription: 'Synchronize data across devices, ensure consistency, and enable offline access',
    maintenanceDescription: 'Schedule system maintenance, perform updates, and ensure optimal performance',
    offlineDescription: 'Work offline without internet connection and sync when online',
    mobileDescription: 'Access your farm data on mobile devices, manage operations on the go',
    collaborationDescription: 'Work with team members, share responsibilities, and collaborate effectively',
    integrationDescription: 'Connect with other farm management tools, sync data, and streamline workflows',
    automationDescription: 'Automate routine tasks, set up workflows, and improve efficiency',
    forecastingDescription: 'Predict future trends, plan ahead, and make data-driven decisions',
    planningDescription: 'Plan farm activities, schedule tasks, and optimize resource allocation',
    monitoringDescription: 'Monitor real-time farm conditions, track progress, and receive alerts',
    alertsDescription: 'Configure custom alerts, receive notifications, and stay informed',
    historyDescription: 'View historical data, analyze trends, and track progress over time',
    performanceDescription: 'Monitor performance metrics, track KPIs, and optimize operations',
    efficiencyDescription: 'Improve farm efficiency, reduce waste, and maximize productivity',
    sustainabilityDescription: 'Monitor sustainable practices, track environmental impact, and improve eco-friendliness',
    complianceDescription: 'Ensure regulatory compliance, maintain records, and follow guidelines',
    documentationDescription: 'Maintain proper documentation, keep records, and ensure traceability',
    trainingDescription: 'Access training materials, learn best practices, and improve skills',

    // Auth
    signInToAccount: 'Sign in to your account',
    welcomeBack: 'Welcome back! Please enter your details.',
    createYourAccount: 'Create your account',
    joinKisanSetu: 'Join KisanSetu to start managing your farm efficiently',
    joinFarmManager: 'Join FarmManager to start managing your farm efficiently',
    dontHaveAccount: "Don't have an account?",
    createOneHere: 'Create one here',
    alreadyHaveAccount: 'Already have an account?',
    signInHere: 'Sign in here',
    signIn: 'Sign In',
    username: 'Username',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    createAccount: 'Create Account',
    
    // Auth Error Messages
    errorPasswordLength: 'Password must be at least 8 characters long',
    errorPasswordUppercase: 'Password must contain at least one uppercase letter',
    errorPasswordLowercase: 'Password must contain at least one lowercase letter',
    errorPasswordNumber: 'Password must contain at least one number',
    errorUsernameLength: 'Username must be 3-50 characters long',
    errorUsernameFormat: 'Username can only contain letters, numbers, and underscores',
    errorPasswordsNoMatch: 'Passwords do not match',
    errorUsernameExists: 'Username already exists or invalid input',
    errorServerError: 'Server error. Please try again later.',
    errorRegistrationFailed: 'Registration failed. Please try again.',
    errorInvalidCredentials: 'Invalid username or password',
    errorNetworkError: 'Network error. Please check your connection.',
    errorLoginFailed: 'Login failed. Please try again.',

    // Additional Labour Management Keys
    labourGroupDetails: 'Labour Group Details',
    groupMembers: 'Group Members',
    totalGroups: 'Total Groups',
    totalLabourers: 'Total Labourers',
    activeGroups: 'Active Groups',
    inactiveGroups: 'Inactive Groups',
    groupStatistics: 'Group Statistics',
    labourerDetails: 'Labourer Details',
    personalInfo: 'Personal Information',
    workInfo: 'Work Information',
    paymentHistory: 'Payment History',
    attendanceRecord: 'Attendance Record',
    totalPayments: 'Total Payments',
    pendingPayments: 'Pending Payments',
    completedPayments: 'Completed Payments',
    paymentStatistics: 'Payment Statistics',
    attendanceStatistics: 'Attendance Statistics',
    workDays: 'Work Days',
    holidays: 'Holidays',
    absentDays: 'Absent Days',
    presentDays: 'Present Days',
    halfDays: 'Half Days',
    overtime: 'Overtime',
    bonus: 'Bonus',
    deduction: 'Deduction',
    advancePayment: 'Advance Payment',
    loanPayment: 'Loan Payment',
    salary: 'Salary',
    wages: 'Wages',
    hourlyRate: 'Hourly Rate',
    monthlySalary: 'Monthly Salary',
    weeklyWages: 'Weekly Wages',
    dailyWages: 'Daily Wages',
    workSchedule: 'Work Schedule',
    shift: 'Shift',
    department: 'Department',
    position: 'Position',
    role: 'Role',
    skills: 'Skills',
    experience: 'Experience',
    qualification: 'Qualification',
    education: 'Education',
    emergencyContact: 'Emergency Contact',
    bankDetails: 'Bank Details',
    accountNumber: 'Account Number',
    bankName: 'Bank Name',
    ifscCode: 'IFSC Code',
    panCard: 'PAN Card',
    aadharCard: 'Aadhar Card',
    dateOfBirth: 'Date of Birth',
    age: 'Age',
    gender: 'Gender',
    maritalStatus: 'Marital Status',
    bloodGroup: 'Blood Group',
    address: 'Address',
    city: 'City',
    state: 'State',
    pincode: 'Pincode',
    country: 'Country',

    // Additional Lot Numbers Keys
    lotDetails: 'Lot Details',
    lotInformation: 'Lot Information',
    lotHistory: 'Lot History',
    lotStatistics: 'Lot Statistics',
    storageDetails: 'Storage Details',
    location: 'Location',
    warehouse: 'Warehouse',
    section: 'Section',
    rack: 'Rack',
    shelf: 'Shelf',
    bin: 'Bin',
    capacity: 'Capacity',
    availableSpace: 'Available Space',
    usedSpace: 'Used Space',
    lotStatus: 'Lot Status',
    quality: 'Quality',
    grade: 'Grade',
    variety: 'Variety',
    size: 'Size',
    weight: 'Weight',
    dimensions: 'Dimensions',
    temperature: 'Temperature',
    humidity: 'Humidity',
    storageConditions: 'Storage Conditions',
    expiryDate: 'Expiry Date',
    manufacturingDate: 'Manufacturing Date',
    batchNumber: 'Batch Number',
    supplier: 'Supplier',
    supplierInfo: 'Supplier Information',
    purchaseDate: 'Purchase Date',
    purchasePrice: 'Purchase Price',
    sellingPrice: 'Selling Price',
    profitMargin: 'Profit Margin',
    inventoryValue: 'Inventory Value',
    stockLevel: 'Stock Level',
    reorderLevel: 'Reorder Level',
    maxCapacity: 'Maximum Capacity',
    currentStock: 'Current Stock',
    reservedStock: 'Reserved Stock',
    availableStock: 'Available Stock',
    damagedStock: 'Damaged Stock',
    returnedStock: 'Returned Stock',
    transferredStock: 'Transferred Stock',
    receivedStock: 'Received Stock',
    issuedStock: 'Issued Stock',
    stockMovement: 'Stock Movement',
    transactionHistory: 'Transaction History',
    lotTracking: 'Lot Tracking',
    traceability: 'Traceability',
    qualityCheck: 'Quality Check',
    inspectionReport: 'Inspection Report',
    certification: 'Certification',
    compliance: 'Compliance',
    safety: 'Safety',
    handling: 'Handling',
    packaging: 'Packaging',
    labeling: 'Labeling',
    barcode: 'Barcode',
    qrCode: 'QR Code',
    serialNumber: 'Serial Number',
    uniqueId: 'Unique ID',

    // Dashboard Statistics
    totalExpenses: 'Total Expenses',
    totalRecords: 'Total Records',
    thisMonth: 'This Month',
    exportPdf: 'Export PDF',
    totalReturned: 'Total Returned',
    pendingReturns: 'Pending Returns',
    totalLots: 'Total Lots',
    searchLotNumber: 'Search Lot Number',

    // Dashboard
    farmDashboard: 'Farm Dashboard',
    overview: 'Overview of your farm\'s performance and metrics',
    totalFields: 'Total Fields',
    totalYield: 'Total Yield',
    laborCost: 'Labor Cost',
    labourEarnings: 'Labour Earnings',
    yieldByPotatoType: 'Yield by Potato Type',
    yieldDistributionSubtitle: 'Distribution of yield across potato categories',
    sevenDayForecast: '7-Day Forecast',
    weatherAlert: 'Weather Alert',
    large: 'Large',
    medium: 'Medium',
    small: 'Small',
    overlarge: 'Overlarge',
    packets: 'packets',
    totalYieldLabel: 'Total Yield',
    totalTransported: 'Total Transported',
    weatherForecast: '7-Day Weather Forecast',
    currentWeather: 'Current Weather',
    gettingLocation: 'Getting location...',
    locationAccessDenied: 'Location access denied',
    locationNotSupported: 'Location not supported',
    locationError: 'Location error occurred',
    retry: 'Retry',
    locationAccessRequired: 'Location access is required for weather data',
    enableLocationAccess: 'Please enable location access in your browser settings to see weather for your current location',
    tryAgain: 'Try Again',
    failedToGetWeatherData: 'Failed to get weather data',
    geolocationNotSupported: 'Geolocation is not supported in your browser',
    fetchingWeatherData: 'Fetching weather data...',
    weatherDataFetched: 'Weather data fetched successfully',

    // Fields
    noLabourersInGroup: 'No labourers in this group',
    fieldsManagement: 'Fields Management',
    addNewField: 'Add New Field',
    fieldName: 'Field Name',
    location: 'Location',
    size: 'Size (acres)',
    potatoType: 'Potato Type',
    plantingDate: 'Planting Date',
    expectedHarvestDate: 'Expected Harvest Date',
    addField: 'Add Field',
    editField: 'Edit Field',
    deleteField: 'Delete Field',
    saveChanges: 'Save Changes',
    cancel: 'Cancel',
    selectField: 'Select Field',
    xLargePackets: 'Extra Large Packets',
    fieldAddedSuccessfully: 'Field added successfully',
    searchFields: 'Search fields...',
    noFieldsFound: 'No fields found',
    fieldUpdatedSuccessfully: 'Field updated successfully',
    fieldDeletedSuccessfully: 'Field deleted successfully',
    groupCreatedSuccessfully: 'Group created successfully',
    yieldAddedSuccessfully: 'Yield record added successfully!',
    yieldUpdatedSummed: 'Yield record updated successfully! Values have been summed with existing record.',
    yieldDeletedSuccessfully: 'Yield record deleted successfully',
    transportationAddedSuccessfully: 'Transportation record added successfully! Lot numbers updated accordingly.',
    lotNumberAddedSuccessfully: 'Lot number added successfully',
    groupUpdatedSuccessfully: 'Group updated successfully',
    labourerCreatedSuccessfully: 'Labourer created successfully',
    labourerUpdatedSuccessfully: 'Labourer updated successfully',
    labourerDeletedSuccessfully: 'Labourer deleted successfully',
    paymentRecordedSuccessfully: 'Payment recorded successfully',
    recordAddedSuccessfully: 'Record added successfully',
    recordUpdatedSuccessfully: 'Record updated successfully',
    labourGroupDeletedSuccessfully: 'Labour group deleted successfully',
    borrowingAddedSuccessfully: 'Borrowing added successfully!',
    borrowingDeletedSuccessfully: 'Borrowing deleted successfully',
    borrowingUpdatedSuccessfully: 'Borrowing updated successfully!',
    borrowingReturnedSuccessfully: 'Borrowing marked as returned and money record created successfully!',
    year: 'Year',

    // Money Records
    moneyRecords: 'Money Records',
    addNewRecord: 'Add New Record',
    recordType: 'Record Type',
    amount: 'Amount',
    description: 'Description',
    date: 'Date',
    income: 'Income',
    expense: 'Expense',
    addRecord: 'Add Record',
    editRecord: 'Edit Record',
    deleteRecord: 'Delete Record',
    updateRecord: 'Update Record',
    totalIncome: 'Total Income',
    totalExpense: 'Total Expense',
    netBalance: 'Net Balance',
    noRecordsFound: 'No records found',
    addYourFirstRecord: 'Add your first record',
    searchByPaidToAmountMethodNotesDate: 'Search by paid to, amount, method, notes, or date',
    records: 'Records',
    PAIDTO: 'Paid To',
    paidTo: 'Paid To',
    paymentMethod: 'Payment Method',
    enterRecipientName: 'Enter recipient name',
    enterAmount: 'Enter amount',
    selectPaymentMethod: 'Select payment method',
    enterAnyAdditionalNotes: 'Enter any additional notes',
    

    // Borrowings
    borrowingsManagement: 'Borrowings Management',
    addNewBorrowing: 'Add New Borrowing',
    lenderName: 'Lender Name',
    borrowingAmount: 'Borrowing Amount',
    interestRate: 'Interest Rate',
    borrowingDate: 'Borrowing Date',
    repaymentDate: 'Repayment Date',
    status: 'Status',
    pending: 'Pending',
    completed: 'Completed',
    overdue: 'Overdue',
    addBorrowing: 'Add Borrowing',
    editBorrowing: 'Edit Borrowing',
    deleteBorrowing: 'Delete Borrowing',
    updateBorrowing: 'Update Borrowing',
    totalBorrowed: 'Total Borrowed',
    totalRepaid: 'Total Repaid',
    outstandingAmount: 'Outstanding Amount',
    noBorrowingsFound: 'No borrowings found',
    addYourFirstBorrowing: 'Add your first borrowing',
    borrowingRecordNotFound: 'Borrowing record not found',
    alreadyReturned: 'This borrowing has already been marked as returned',
    
    // New Borrowing Keys
    borrowerName: 'Borrower Name',
    enterBorrowerName: 'Enter borrower name',
    borrowDate: 'Borrow Date',
    expectedReturnDate: 'Expected Return Date',
    actualReturn: 'Actual Return',
    actualReturnDate: 'Actual Return Date',
    returned: 'Returned',
    noBorrowingRecordsYet: 'No borrowing records yet',
    startTrackingBorrowings: 'Start tracking your borrowings by adding your first record',
    borrowedOn: 'Borrowed on',
    expectedReturn: 'Expected return',
    returnedOn: 'Returned on',
    markReturned: 'Mark Returned',
    pleaseFillRequiredFieldsBorrowing: 'Please fill in all required fields: Borrower Name, Amount, and Borrow Date',
    enterValidAmount: 'Please enter a valid amount greater than 0',
    failedToCreateBorrowing: 'Failed to create borrowing record. Please try again.',
    failedToUpdateBorrowing: 'Failed to update borrowing record. Please try again.',
    failedToDeleteBorrowing: 'Failed to delete borrowing record. Please try again.',
    failedToReturnBorrowing: 'Failed to update borrowing status and create money record. Please try again.',

    // Lot Numbers
    lotNumbersManagement: 'Lot Numbers Management',
    lotNumberManagement: 'Lot Numbers Management', // Alternate key for compatibility
    addNewLotNumber: 'Add New Lot Number',
    lotNumberName: 'Lot Number',
    category: 'Category',
    packetCount: 'Packet Count',
    storageDate: 'Storage Date',
    notes: 'Notes',
    addLotNumber: 'Add Lot Number',
    editLotNumber: 'Edit Lot Number',
    deleteLotNumber: 'Delete Lot Number',
    updateLotNumber: 'Update Lot Number',
    totalPackets: 'Total Packets',
    noLotNumbersFound: 'No lot numbers found',
    addYourFirstLotNumber: 'Add your first lot number',

    // Labour Management
    noLabourGroupsFound: 'No labour groups found',
    addYourFirstLabourGroup: 'Add your first labour group',
    noLabourersFound: 'No labourers found',
    addYourFirstLabourer: 'Add your first labourer',
    noPaymentsFound: 'No payments found',
    addYourFirstPayment: 'Add your first payment',
    startTrackingExpenses: 'Start tracking your expenses',
    noMoneyRecordsYet: 'No money records yet',
    earningsCalculation: 'Earnings Calculation',
    noPaymentRecords: 'No payment records found',
    groupSummary: 'Group Summary',
    totalEarnings: 'Total Earnings',
    totalPaid: 'Total Paid',
    TOTALEARNINGS: 'Total Earnings',
    TOTALPAID: 'Total Paid',
    labourManagement: 'Labour Management',
    manageLabourGroups: 'Manage Labour Groups',
    manageLabourers: 'Manage Labourers',
    labourGroups: 'Labour Groups',
    addGroup: 'Add Group',
    groupName: 'Group Name',
    enterGroupName: 'Enter group name',
    createGroup: 'Create Group',
    updateGroup: 'Update Group',
    deleteGroup: 'Delete Group',
    labourers: 'Labourers',
    addLabourer: 'Add Labourer',
    name: 'Name',
    village: 'Village',
    dailyWage: 'Daily Wage',
    phoneNumber: 'Phone Number',
    enterName: 'Enter name',
    enterVillage: 'Enter village',
    enterDailyWage: 'Enter daily wage',
    enterPhoneNumber: 'Enter phone number',
    selectGroup: 'Select Group',
    createLabourer: 'Create Labourer',
    updateLabourer: 'Update Labourer',
    deleteLabourer: 'Delete Labourer',
    payments: 'Payments',
    addPayment: 'Add Payment',
    paymentDate: 'Payment Date',
    workingDays: 'Working Days',
    paymentType: 'Payment Type',
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    enterNotes: 'Enter notes',
    createPayment: 'Create Payment',
    updatePayment: 'Update Payment',
    deletePayment: 'Delete Payment',
    totalPayments: 'Total Payments',
    noPaymentsFound: 'No payments found',
    addYourFirstPayment: 'Add your first payment',
    totalWorkingDays: 'Total Working Days',
    paymentHistory: 'Payment History',
    paymentDetails: 'Payment Details',
    financialSummary: 'Financial Summary',
    exportAsPDF: 'Export as PDF',
    generatedOn: 'Generated on',
    summaryStatistics: 'Summary Statistics',
    labourersDetails: 'Labourers Details',
    recentPaymentHistory: 'Recent Payment History',
    noteAttendanceSystem: 'Note: Working day attendance system will be updated when implemented.',
    attendance: 'Attendance',
    attendanceDate: 'Attendance Date',
    attendanceStatus: 'Attendance Status',
    fullDay: 'Full Day',
    halfDay: 'Half Day',
    absent: 'Absent',
    saveAttendance: 'Save Attendance',
    attendanceTodayOnly: 'Attendance can only be marked for today.',
    exportAttendancePDF: 'Export Attendance PDF',
    failedToFetchAttendance: 'Failed to fetch attendance',
    failedToSaveAttendance: 'Failed to save attendance',
    attendanceMarkedSuccessfully: 'Attendance has been marked successfully',
    joiningDate: 'Joining Date',
    totalWages: 'Total Wages',
    amountToPay: 'Amount To Pay',
    overpaidAmount: 'Overpaid Amount',

    // Work Tracker
    workTracker: 'Work Tracker',
    exportWorkPDF: 'Export Work PDF',
    saveWork: 'Save Work',
    totalPackets: 'Total Packets',
    smallPackets: 'Small Packets',
    mediumPackets: 'Medium Packets',
    largePackets: 'Large Packets',
    overlargePackets: 'Overlarge Packets',
    addNotes: 'Add Notes',
    noWorkRecords: 'No work records found',
    tillDate: 'Till Date',
    today: 'Today',
    cumulativeTotalPackets: 'Cumulative Total Packets',
    totalReceived: 'Total Received',
    totalWorkingDays: 'Total Working Days',
    paymentLogs: 'Payment Logs',
    pay: 'Pay',


    // Transportation
    transportation: 'Transportation',
    transportationManagement: 'Transportation Management',
    transportationManagementDescription: 'Track potato packets transported from fields to cold storage with lot number allocation.',
    addTransportation: 'Add Transportation',
    transportationRecords: 'Transportation Records',
    noTransportationRecordsYet: 'No transportation records yet',
    startTrackingTransportations: 'Start tracking your field-to-storage transportation',
    addYourFirstTransportation: 'Add your first transportation',
    transportDate: 'Transport Date',
    lotNumber: 'Lot Number',
    enterLotNumber: 'Enter lot number',
    updateTransportation: 'Update Transportation',

    // Missing Common Keys
    exportPDF: 'Export PDF',
    searchLotNumbers: 'Search Lot Numbers',
    allFields: 'All Fields',
    hideForm: 'Hide Form',
    enterLotNumber: 'Enter Lot Number',
    enterFieldName: 'Enter Field Name',
    enterSmallPackets: 'Enter Small Packets',
    enterMediumPackets: 'Enter Medium Packets',
    enterLargePackets: 'Enter Large Packets',
    enterXlargePackets: 'Enter Extra Large Packets',
    enterNotes: 'Enter Notes',
    pleaseFillAllRequiredFields: 'Please fill all required fields',
    pleaseEnterValidPacketCount: 'Please enter a valid packet count',
    failedToCreateLotNumber: 'Failed to create lot number',
    failedToUpdateLotNumber: 'Failed to update lot number',
    confirmDeleteLotNumber: 'Are you sure you want to delete this lot number?',
    failedToDeleteLotNumber: 'Failed to delete lot number',
    getStartedByAddingLotNumber: 'Get started by adding your first lot number',
    storedOn: 'Stored on',
    saving: 'Saving...',
    adding: 'Adding...',
    updating: 'Updating...',
    enterAreaInAcres: 'Enter area in acres',
    enterYear: 'Enter year',
    noFieldsYet: 'No fields yet',
    getStartedByAddingFirstField: 'Get started by adding your first field',
    addYourFirstField: 'Add your first field',
    updateField: 'Update Field',
    yieldRecords: 'Yield Records',
    addYield: 'Add Yield',
    noYieldRecordsYet: 'No yield records yet',
    acres: 'acres',
    area: 'Area',
    updateYield: 'Update Yield',
    weatherAlertPrefix: 'Alert: Rain/Storm expected on -',
    takePrecautions: 'Please take necessary precautions.',
    weatherClearForecast: 'Weather is expected to remain clear for the next 7 days. Good conditions for field work.',

    // Additional translation keys
    actions: 'Actions',
    field: 'Field',
    enterLargePotatoes: 'Enter large potatoes',
    enterMediumPotatoes: 'Enter medium potatoes',
    enterSmallPotatoes: 'Enter small potatoes',
    enterOverlargePotatoes: 'Enter overlarge potatoes',
    optionalNotes: 'Optional notes',
    
    // Daily Report
    dailyReport: 'Daily Report (PDF)',
    downloadDailyReport: 'Download today\'s activity report',
    noActivitiesToday: 'No new activities recorded for today.',
    reportGeneratedAt: 'Generated at',
    reportDate: 'Report Date'
  },
  hi: {
    // App
    year: 'साल',
    appName: 'किसान सेतु',
    // Navigation
    home: 'होम',
    navDashboard: 'डैशबोर्ड',
    navFields: 'खेत',
    navMoney: 'मनी रिकॉर्ड',
    navBorrowings: 'उधार',
    navLotNumbers: 'लॉट नंबर',
    navTransportation: 'परिवहन',
    navLabour: 'श्रम प्रबंधन',
    logout: 'लॉगआउट',
    login: 'लॉगिन',
    signup: 'साइन अप',
    welcome: 'स्वागत है',
    hi: 'नमस्ते',

    // Common UI Elements
    METHOD: 'तरीका',
    PAIDTO: 'किसे भुगतान किया',
    addMoneyRecord: 'मनी रिकॉर्ड जोड़ें',
    addNewBorrowing: 'नया उधार जोड़ें',
    save: 'सेव करें',
    cancel: 'रद्द करें',
    delete: 'हटाएं',
    edit: 'संपादित करें',
    add: 'जोड़ें',
    update: 'अपडेट करें',
    search: 'खोजें',
    filter: 'फिल्टर',
    loading: 'लोड हो रहा है...',
    error: 'त्रुटि',
    success: 'सफलता',
    warning: 'चेतावनी',
    info: 'जानकारी',
    confirm: 'पुष्टि करें',
    yes: 'हां',
    no: 'नहीं',
    close: 'बंद करें',
    back: 'पीछे',
    next: 'अगला',
    previous: 'पिछला',
    submit: 'जमा करें',
    reset: 'रीसेट करें',
    clear: 'साफ करें',
    select: 'चुनें',
    view: 'देखें',
    download: 'डाउनलोड',
    export: 'निर्यात',
    import: 'आयात',
    print: 'प्रिंट',
    refresh: 'रिफ्रेश',
    settings: 'सेटिंग्स',
    profile: 'प्रोफाइल',
    admin: 'एडमिन',
    user: 'उपयोगकर्ता',
    date: 'तिथि',
    time: 'समय',
    name: 'नाम',
    email: 'ईमेल',
    phone: 'फोन',
    address: 'पता',
    status: 'स्थिति',
    active: 'सक्रिय',
    inactive: 'निष्क्रिय',
    total: 'कुल',
    count: 'गिनती',
    amount: 'राशि',
    quantity: 'मात्रा',
    price: 'मूल्य',
    cost: 'लागत',
    revenue: 'राजस्व',
    profit: 'लाभ',
    loss: 'हानि',
    balance: 'शेष',
    payment: 'भुगतान',
    receipt: 'रसीद',
    invoice: 'चालान',
    order: 'ऑर्डर',
    delivery: 'डिलीवरी',
    shipping: 'शिपिंग',
    tracking: 'ट्रैकिंग',
    notes: 'नोट्स',
    description: 'विवरण',
    details: 'विवरण',
    history: 'इतिहास',
    log: 'लॉग',
    report: 'रिपोर्ट',
    analytics: 'एनालिटिक्स',
    statistics: 'आंकड़े',
    about: 'के बारे में',
    contact: 'संपर्क',
    help: 'मदद',
    support: 'समर्थन',
    faq: 'अक्सर पूछे जाने वाले प्रश्न',
    terms: 'नियम और शर्तें',
    privacy: 'गोपनीयता',
    legal: 'कानूनी',
    copyright: 'कॉपीराइट',
    version: 'संस्करण',
    updateAvailable: 'अपडेट उपलब्ध',
    maintenance: 'रखरखाव',
    offline: 'ऑफलाइन',
    online: 'ऑनलाइन',
    connected: 'कनेक्टेड',
    disconnected: 'डिस्कनेक्टेड',
    syncing: 'सिंक हो रहा है',
    synced: 'सिंक हो गया',
    failed: 'विफल',
    pending: 'लंबित',
    completed: 'पूर्ण',
    cancelled: 'रद्द',
    rejected: 'अस्वीकृत',
    approved: 'स्वीकृत',
    processing: 'प्रसंस्करण',
    processed: 'प्रसंस्कृत',
    queued: 'कतार में',
    scheduled: 'अनुसूचित',
    ongoing: 'चल रहा है',
    finished: 'समाप्त',
    started: 'शुरू',
    stopped: 'रुका हुआ',
    paused: 'रुका हुआ',
    resumed: 'फिर शुरू',

    // Subtitles and Descriptions
    usernameExists: 'यह उपयोगकर्ता नाम पहले से मौजूद है',
    noLabourGroupsFound: 'कोई श्रम समूह नहीं मिला',
    noLabourersInGroup: 'इस समूह में कोई मजदूर नहीं है',
    welcomeSubtitle: 'आपके खेत प्रबंधन प्रणाली में आपका स्वागत है',
    dashboardSubtitle: 'अपने खेत के प्रदर्शन और गतिविधियों की निगरानी करें',
    fieldsSubtitle: 'अपने कृषि खेतों और फसलों का प्रबंधन करें',
    moneySubtitle: 'आय, व्यय और वित्तीय रिकॉर्ड ट्रैक करें',
    borrowingsSubtitle: 'ऋण और उधार रिकॉर्ड का प्रबंधन करें',
    lotNumbersSubtitle: 'आलू के लॉट और भंडारण जानकारी ट्रैक करें',
    transportationSubtitle: 'कृषि उत्पादों के परिवहन की निगरानी करें',
    labourSubtitle: 'श्रमिकों और वेतन जानकारी का प्रबंधन करें',
    settingsSubtitle: 'एप्लिकेशन प्राथमिकताएँ कॉन्फ़िगर करें',
    profileSubtitle: 'अपने खाते और व्यक्तिगत जानकारी का प्रबंधन करें',
    reportsSubtitle: 'विभिन्न रिपोर्ट उत्पन्न करें और देखें',
    analyticsSubtitle: 'विस्तृत एनालिटिक्स और अंतर्दृष्टि देखें',
    notificationsSubtitle: 'सिस्टम सूचनाएं और अलर्ट का प्रबंधन करें',
    helpSubtitle: 'एप्लिकेशन का उपयोग करने के लिए सहायता और समर्थन प्राप्त करें',
    aboutSubtitle: 'खेत प्रबंधन प्रणाली के बारे में और जानें',
    contactSubtitle: 'समर्थन टीम से संपर्क करें',
    loginSubtitle: 'अपने खेत डैशबोर्ड तक पहुंचने के लिए साइन इन करें',
    signupSubtitle: 'शुरू करने के लिए एक नया खाता बनाएं',
    forgotPasswordSubtitle: 'अगर आप अपना पासवर्ड भूल गए हैं तो रीसेट करें',
    securitySubtitle: 'अपने खाते सुरक्षा सेटिंग्स का प्रबंधन करें',
    privacySubtitle: 'अपने डेटा और गोपनीयता सेटिंग्स नियंत्रित करें',
    backupSubtitle: 'अपने खेत डेटा का बैकअप और पुनर्स्थापन करें',
    exportSubtitle: 'विभिन्न प्रारूपों में अपना डेटा निर्यात करें',
    importSubtitle: 'बाहरी स्रोतों से डेटा आयात करें',
    syncSubtitle: 'डिवाइसों के बीच डेटा सिंक्रोनाइज़ करें',
    maintenanceSubtitle: 'सिस्टम रखरखाव और अपडेट',
    offlineSubtitle: 'ऑफलाइन काम करें और ऑनलाइन आने पर सिंक करें',
    mobileSubtitle: 'मोबाइल उपकरणों पर अपने खेत डेटा तक पहुंचें',
    collaborationSubtitle: 'टीम के सदस्यों और साझेदारों के साथ काम करें',
    integrationSubtitle: 'अन्य खेत प्रबंधन उपकरणों से कनेक्ट करें',
    automationSubtitle: 'रूटीन कार्यों और प्रक्रियाओं को स्वचालित करें',
    forecastingSubtitle: 'भविष्य के रुझान और परिणामों की भविष्यवाणी करें',
    planningSubtitle: 'खेत गतिविधियों की योजना बनाएं और शेड्यूल करें',
    monitoringSubtitle: 'रियल-टाइम खेत स्थितियों की निगरानी करें',
    alertsSubtitle: 'महत्वपूर्ण अलर्ट और सूचनाएं प्राप्त करें',
    historySubtitle: 'ऐतिहासिक डेटा और रुझान देखें',
    performanceSubtitle: 'प्रदर्शन मेट्रिक्स और KPI ट्रैक करें',
    efficiencySubtitle: 'खेत दक्षता और उत्पादकता में सुधार करें',
    sustainabilitySubtitle: 'स्थिरता प्रथाओं की निगरानी और सुधार करें',
    complianceSubtitle: 'नियामक अनुपालन सुनिश्चित करें',
    documentationSubtitle: 'उचित दस्तावेज़ीकरण और रिकॉर्ड बनाए रखें',
    trainingSubtitle: 'प्रशिक्षण सामग्री और गाइड तक पहुंचें',
    supportSubtitle: 'तकनीकी समर्थन और सहायता प्राप्त करें',

    // Page Descriptions
    fieldsManagementDescription: 'अपने कृषि खेतों का प्रबंधन करें, फसल उपज को ट्रैक करें, और खेत प्रदर्शन की निगरानी करें',
    moneyRecordsDescription: 'सभी आय और व्यय ट्रैक करें, वित्तीय रिकॉर्ड का प्रबंधन करें, और नकदी प्रवाह की निगरानी करें',
    borrowingsManagementDescription: 'ऋण का प्रबंधन करें, चुकौती ट्रैक करें, और उधार इतिहास की निगरानी करें',
    lotNumberManagementDescription: 'आलू के लॉट ट्रैक करें, भंडारण जानकारी का प्रबंधन करें, और इन्वेंट्री की निगरानी करें',
    lotNumbersManagementDescription: 'आलू के लॉट ट्रैक करें, भंडारण जानकारी का प्रबंधन करें, और इन्वेंट्री की निगरानी करें',
    transportationManagementDescription: 'खेतों से भंडारण तक कृषि उत्पादों के परिवहन की निगरानी करें',
    labourManagementDescription: 'श्रमिकों का प्रबंधन करें, उपस्थिति ट्रैक करें, वेतन संभालें, और श्रम लागत की निगरानी करें',
    dashboardDescription: 'अपने खेत संचालन और प्रदर्शन मेट्रिक्स का व्यापक अवलोकन देखें',
    settingsDescription: 'एप्लिकेशन सेटिंग्स कॉन्फ़िगर करें, प्राथमिकताओं का प्रबंधन करें, और अपने अनुभव को अनुकूलित करें',
    profileDescription: 'अपनी व्यक्तिगत जानकारी, खाता सेटिंग्स, और उपयोगकर्ता प्राथमिकताओं का प्रबंधन करें',
    reportsDescription: 'विस्तृत रिपोर्ट उत्पन्न करें, रुझान का विश्लेषण करें, और व्यावसायिक अंतर्दृष्टि निर्यात करें',
    analyticsDescription: 'विस्तृत एनालिटिक्स देखें, प्रदर्शन मेट्रिक्स ट्रैक करें, और व्यावसायिक अंतर्दृष्टि प्राप्त करें',
    notificationsDescription: 'सिस्टम सूचनाओं का प्रबंधन करें, अलर्ट सेट करें, और महत्वपूर्ण घटनाओं पर अपडेट रहें',
    helpDescription: 'सहायता दस्तावेज़ीकरण तक पहुंचें, ट्यूटोरियल, और समर्थन संसाधन',
    aboutDescription: 'हमारे खेत प्रबंधन प्रणाली, सुविधाओं, और कंपनी जानकारी के बारे में जानें',
    contactDescription: 'हमारी समर्थन टीम से संपर्क करें, प्रतिक्रिया प्रदान करें, या सहायता का अनुरोध करें',
    loginDescription: 'अपने खेत प्रबंधन डैशबोर्ड तक पहुंचने के लिए अपने खाते में साइन इन करें',
    signupDescription: 'अपना खेत हमारे व्यापक उपकरणों के साथ प्रबंधन करना शुरू करने के लिए एक नया खाता बनाएं',
    forgotPasswordDescription: 'अपना पासवर्ड सुरक्षित रूप से रीसेट करें और अपने खाते तक पहुंच प्राप्त करें',
    securityDescription: 'खाता सुरक्षा का प्रबंधन करें, दो-कारक प्रमाणीकरण सक्षम करें, और अपने डेटा की सुरक्षा करें',
    privacyDescription: 'अपनी गोपनीयता सेटिंग्स नियंत्रित करें, डेटा साझाकरण का प्रबंधन करें, और अपनी जानकारी की सुरक्षा करें',
    backupDescription: 'अपने खेत डेटा का बैकअप करें, पिछले संस्करणों को पुनर्स्थापित करें, और डेटा सुरक्षा सुनिश्चित करें',
    exportDescription: 'विश्लेषण और रिकॉर्ड रखरखाव के लिए विभिन्न प्रारूपों में अपना खेत डेटा निर्यात करें',
    importDescription: 'बाहरी स्रोतों से डेटा आयात करें, जानकारी माइग्रेट करें, और सेटअप सुव्यवस्थित करें',
    syncDescription: 'डिवाइसों के बीच डेटा सिंक्रोनाइज़ करें, निरंतरता सुनिश्चित करें, और ऑफलाइन पहुंच सक्षम करें',
    maintenanceDescription: 'सिस्टम रखरखाव शेड्यूल करें, अपडेट करें, और इष्टतम प्रदर्शन सुनिश्चित करें',
    offlineDescription: 'इंटरनेट कनेक्शन के बिना ऑफलाइन काम करें और ऑनलाइन आने पर सिंक करें',
    mobileDescription: 'मोबाइल उपकरणों पर अपने खेत डेटा तक पहुंचें, चलते-फिरते संचालन का प्रबंधन करें',
    collaborationDescription: 'टीम के सदस्यों के साथ काम करें, जिम्मेदारियां साझा करें, और प्रभावी ढंग से सहयोग करें',
    integrationDescription: 'अन्य खेत प्रबंधन उपकरणों से कनेक्ट करें, डेटा सिंक करें, और वर्कफ़्लो सुव्यवस्थित करें',
    automationDescription: 'रूटीन कार्यों को स्वचालित करें, वर्कफ़्लो सेट करें, और दक्षता में सुधार करें',
    forecastingDescription: 'भविष्य के रुझानों की भविष्यवाणी करें, आगे की योजना बनाएं, और डेटा-आधारित निर्णय लें',
    planningDescription: 'खेत गतिविधियों की योजना बनाएं, कार्य शेड्यूल करें, और संसाधन आवंटन अनुकूलित करें',
    monitoringDescription: 'रियल-टाइम खेत स्थितियों की निगरानी करें, प्रगति ट्रैक करें, और अलर्ट प्राप्त करें',
    alertsDescription: 'कस्टम अलर्ट कॉन्फ़िगर करें, सूचनाएं प्राप्त करें, और सूचित रहें',
    historyDescription: 'ऐतिहासिक डेटा देखें, रुझान का विश्लेषण करें, और समय के साथ प्रगति ट्रैक करें',
    performanceDescription: 'प्रदर्शन मेट्रिक्स की निगरानी करें, KPI ट्रैक करें, और संचालन अनुकूलित करें',
    efficiencyDescription: 'खेत दक्षता में सुधार करें, बर्बादी कम करें, और उत्पादकता अधिकतम करें',
    sustainabilityDescription: 'स्थिरता प्रथाओं की निगरानी करें, पर्यावरणीय प्रभाव ट्रैक करें, और पर्यावरण-अनुकूलता में सुधार करें',
    complianceDescription: 'नियामक अनुपालन सुनिश्चित करें, रिकॉर्ड बनाए रखें, और दिशानिर्देशों का पालन करें',
    documentationDescription: 'उचित दस्तावेज़ीकरण बनाए रखें, रिकॉर्ड रखें, और ट्रेसबिलिटी सुनिश्चित करें',
    trainingDescription: 'प्रशिक्षण सामग्री तक पहुंचें, सर्वोत्तम अभ्यास सीखें, और कौशल में सुधार करें',

    // Auth
    signInToAccount: 'अपने खाते में साइन इन करें',
    welcomeBack: 'वापसी पर स्वागत है! कृपया अपना विवरण दर्ज करें।',
    createYourAccount: 'अपना खाता बनाएं',
    joinKisanSetu: 'अपने खेत का कुशलतापूर्वक प्रबंधन शुरू करने के लिए किसानसेतु से जुड़ें',
    joinFarmManager: 'अपने खेत का कुशलतापूर्वक प्रबंधन शुरू करने के लिए FarmManager से जुड़ें',
    dontHaveAccount: 'खाता नहीं है?',
    createOneHere: 'यहाँ एक बनाएँ',
    alreadyHaveAccount: 'क्या आपके पास पहले से एक खाता मौजूद है?',
    signInHere: 'यहाँ साइन इन करें',
    signIn: 'साइन इन',
    username: 'उपयोगकर्ता नाम',
    password: 'पासवर्ड',
    confirmPassword: 'पासवर्ड की पुष्टि करें',
    createAccount: 'खाता बनाएं',
    
    // Auth Error Messages
    errorPasswordLength: 'पासवर्ड कम से कम 8 वर्णों का होना चाहिए',
    errorPasswordUppercase: 'पासवर्ड में कम से कम एक बड़ा अक्षर होना चाहिए',
    errorPasswordLowercase: 'पासवर्ड में कम से कम एक छोटा अक्षर होना चाहिए',
    errorPasswordNumber: 'पासवर्ड में कम से कम एक नंबर होना चाहिए',
    errorUsernameLength: 'उपयोगकर्ता नाम 3-50 वर्णों का होना चाहिए',
    errorUsernameFormat: 'उपयोगकर्ता नाम में केवल अक्षर, संख्या और अंडरस्कोर हो सकते हैं',
    errorPasswordsNoMatch: 'पासवर्ड मेल नहीं खाते',
    errorUsernameExists: 'उपयोगकर्ता नाम पहले से मौजूद है या अमान्य इनपुट',
    errorServerError: 'सर्वर त्रुटि। कृपया बाद में पुन: प्रयास करें।',
    errorRegistrationFailed: 'पंजीकरण विफल रहा। कृपया पुन: प्रयास करें।',
    errorInvalidCredentials: 'अमान्य उपयोगकर्ता नाम या पासवर्ड',
    errorNetworkError: 'नेटवर्क त्रुटि। कृपया अपना कनेक्शन जांचें।',
    errorLoginFailed: 'लॉगिन विफल रहा। कृपया पुन: प्रयास करें।',

    // Work Tracker
    workTracker: 'कार्य ट्रैकर',
    exportWorkPDF: 'कार्य पीडीएफ निर्यात करें',
    saveWork: 'कार्य सहेजें',
    totalPackets: 'कुल पैकेट',
    smallPackets: 'छोटे पैकेट',
    mediumPackets: 'मध्यम पैकेट',
    largePackets: 'बड़े पैकेट',
    overlargePackets: 'अतिरिक्त बड़े पैकेट',
    addNotes: 'नोट्स जोड़ें',
    noWorkRecords: 'कोई कार्य रिकॉर्ड नहीं मिला',
    tillDate: 'आज तक',
    today: 'आज',
    cumulativeTotalPackets: 'संचयी कुल पैकेट',
    totalReceived: 'कुल प्राप्त',
    totalWorkingDays: 'कुल कार्य दिवस',
    paymentLogs: 'भुगतान लॉग्स',
    pay: 'भुगतान करें',
    noMoneyRecordsYet: 'अभी तक कोई मनी रिकॉर्ड नहीं है',
    startTrackingExpenses: 'अपने खर्चों को ट्रैक करना शुरू करें',

    // Additional Labour Management Keys
    noLabourGroupsFound: 'कोई श्रम समूह नहीं मिला',
    addYourFirstLabourGroup: 'अपना पहला श्रम समूह जोड़ें',
    noLabourersFound: 'कोई मजदूर नहीं मिला',
    addYourFirstLabourer: 'अपना पहला मजदूर जोड़ें',
    noPaymentsFound: 'कोई भुगतान रिकॉर्ड नहीं मिला',
    addYourFirstPayment: 'अपना पहला भुगतान जोड़ें',
    labourGroupDetails: 'श्रम समूह विवरण',
    groupMembers: 'समूह सदस्य',
    totalGroups: 'कुल समूह',
    totalLabourers: 'कुल मजदूर',
    activeGroups: 'सक्रिय समूह',
    inactiveGroups: 'निष्क्रिय समूह',
    groupStatistics: 'समूह आँकड़े',
    labourerDetails: 'मजदूर विवरण',
    personalInfo: 'व्यक्तिगत जानकारी',
    workInfo: 'कार्य जानकारी',
    paymentHistory: 'भुगतान इतिहास',
    attendanceRecord: 'उपस्थिति रिकॉर्ड',
    totalPayments: 'कुल भुगतान',
    pendingPayments: 'लंबित भुगतान',
    completedPayments: 'पूर्ण भुगतान',
    paymentStatistics: 'भुगतान आँकड़े',
    attendanceStatistics: 'उपस्थिति आँकड़े',
    workDays: 'कार्य दिवस',
    holidays: 'छुट्टियाँ',
    absentDays: 'अनुपस्थिति दिवस',
    presentDays: 'उपस्थिति दिवस',
    halfDays: 'आधे दिवस',
    overtime: 'ओवरटाइम',
    bonus: 'बोनस',
    deduction: 'कटौती',
    advancePayment: 'अग्रिम भुगतान',
    loanPayment: 'ऋण भुगतान',
    salary: 'वेतन',
    wages: 'मजदूरी',
    hourlyRate: 'प्रति घंटा दर',
    monthlySalary: 'मासिक वेतन',
    weeklyWages: 'साप्ताहिक मजदूरी',
    dailyWages: 'दैनिक मजदूरी',
    workSchedule: 'कार्य अनुसूची',
    shift: 'शिफ्ट',
    department: 'विभाग',
    position: 'पद',
    role: 'भूमिका',
    skills: 'कौशल',
    experience: 'अनुभव',
    qualification: 'योग्यता',
    education: 'शिक्षा',
    emergencyContact: 'आपातकालीन संपर्क',
    bankDetails: 'बैंक विवरण',
    accountNumber: 'खाता संख्या',
    bankName: 'बैंक का नाम',
    ifscCode: 'IFSC कोड',
    panCard: 'PAN कार्ड',
    aadharCard: 'आधार कार्ड',
    dateOfBirth: 'जन्म तिथि',
    age: 'आयु',
    gender: 'लिंग',
    maritalStatus: 'वैवाहिक स्थिति',
    bloodGroup: 'रक्त समूह',
    address: 'पता',
    city: 'शहर',
    state: 'राज्य',
    pincode: 'पिनकोड',
    country: 'देश',
    groupSummary: 'समूह सारांश',
    totalEarnings: 'कुल कमाई',
    totalPaid: 'कुल भुगतान',
    TOTALEARNINGS: 'कुल कमाई',
    TOTALPAID: 'कुल भुगतान',
    earningsCalculation: 'कमाई की गणना',
    noPaymentRecords: 'कोई भुगतान रिकॉर्ड नहीं मिला',

    // Additional Lot Numbers Keys
    lotDetails: 'लॉट विवरण',
    lotInformation: 'लॉट जानकारी',
    lotHistory: 'लॉट इतिहास',
    lotStatistics: 'लॉट आँकड़े',
    storageDetails: 'भंडारण विवरण',
    location: 'स्थान',
    warehouse: 'गोदाम',
    section: 'अनुभाग',
    rack: 'रैक',
    shelf: 'शेल्फ',
    bin: 'बिन',
    capacity: 'क्षमता',
    availableSpace: 'उपलब्ध स्थान',
    usedSpace: 'उपयोग किया गया स्थान',
    lotStatus: 'लॉट स्थिति',
    quality: 'गुणवत्ता',
    grade: 'ग्रेड',
    variety: 'किस्म',
    size: 'आकार',
    weight: 'वजन',
    dimensions: 'आयाम',
    temperature: 'तापमान',
    humidity: 'आर्द्रता',
    storageConditions: 'भंडारण की स्थितियाँ',
    expiryDate: 'समाप्ति तिथि',
    manufacturingDate: 'निर्माण तिथि',
    batchNumber: 'बैच संख्या',
    supplier: 'आपूर्तिकर्ता',
    supplierInfo: 'आपूर्तिकर्ता जानकारी',
    purchaseDate: 'खरीद तिथि',
    purchasePrice: 'खरीद मूल्य',
    sellingPrice: 'बिक्री मूल्य',
    profitMargin: 'लाभ मार्जिन',
    inventoryValue: 'इन्वेंट्री मूल्य',
    stockLevel: 'स्टॉक स्तर',
    reorderLevel: 'रीऑर्डर स्तर',
    maxCapacity: 'अधिकतम क्षमता',
    currentStock: 'वर्तमान स्टॉक',
    reservedStock: 'आरक्षित स्टॉक',
    availableStock: 'उपलब्ध स्टॉक',
    damagedStock: 'क्षतिग्रस्त स्टॉक',
    returnedStock: 'लौटाया गया स्टॉक',
    transferredStock: 'स्थानांतरित स्टॉक',
    receivedStock: 'प्राप्त स्टॉक',
    issuedStock: 'जारी किया गया स्टॉक',
    stockMovement: 'स्टॉक आंदोलन',
    transactionHistory: 'लेनदेन इतिहास',
    lotTracking: 'लॉट ट्रैकिंग',
    traceability: 'ट्रेसबिलिटी',
    qualityCheck: 'गुणवत्ता जांच',
    inspectionReport: 'निरीक्षण रिपोर्ट',
    certification: 'प्रमाणन',
    compliance: 'अनुपालन',
    safety: 'सुरक्षा',
    handling: 'हैंडलिंग',
    packaging: 'पैकेजिंग',
    labeling: 'लेबलिंग',
    barcode: 'बारकोड',
    qrCode: 'क्यूआर कोड',
    serialNumber: 'सीरियल नंबर',
    uniqueId: 'अद्वितीय आईडी',

    // Transportation (Hindi)
    transportation: 'परिवहन',
    transportationManagement: 'परिवहन प्रबंधन',
    transportationManagementDescription: 'खेतों से कोल्ड स्टोरेज तक आलू के पैकेट्स के परिवहन को लॉट नंबर आवंटन के साथ ट्रैक करें।',
    addTransportation: 'परिवहन जोड़ें',
    transportationRecords: 'परिवहन रिकॉर्ड',
    noTransportationRecordsYet: 'अभी तक कोई परिवहन रिकॉर्ड नहीं',
    startTrackingTransportations: 'अपने खेत-से-स्टोरेज परिवहन को ट्रैक करना शुरू करें',
    addYourFirstTransportation: 'अपना पहला परिवहन जोड़ें',
    transportDate: 'परिवहन तिथि',
    lotNumber: 'लॉट नंबर',
    enterLotNumber: 'लॉट नंबर दर्ज करें',
    updateTransportation: 'परिवहन अपडेट करें',
    xLargePackets: 'अतिरिक्त बड़े पैकेट',

    // Missing Common Keys (Hindi)
    fieldAddedSuccessfully: 'खेत सफलतापूर्वक जोड़ा गया',
    searchFields: 'खेत खोजें...',
    noFieldsFound: 'कोई खेत नहीं मिला',
    fieldUpdatedSuccessfully: 'खेत सफलतापूर्वक अपडेट किया गया', 
    fieldDeletedSuccessfully: 'खेत सफलतापूर्वक हटाया गया',
    yieldAddedSuccessfully: 'उपज रिकॉर्ड सफलतापूर्वक जोड़ा गया!',
    yieldUpdatedSummed: 'उपज रिकॉर्ड सफलतापूर्वक अपडेट किया गया! मान मौजूदा रिकॉर्ड के साथ जोड़ दिए गए हैं।',
    yieldDeletedSuccessfully: 'उपज रिकॉर्ड सफलतापूर्वक हटा दिया गया',
    transportationAddedSuccessfully: 'परिवहन रिकॉर्ड सफलतापूर्वक जोड़ा गया! लॉट नंबर तदनुसार अपडेट किए गए।',
    lotNumberAddedSuccessfully: 'लॉट नंबर सफलतापूर्वक जोड़ा गया',
    groupCreatedSuccessfully: 'समूह सफलतापूर्वक बनाया गया',
    groupUpdatedSuccessfully: 'समूह सफलतापूर्वक अपडेट किया गया',
    labourerCreatedSuccessfully: 'मजदूर सफलतापूर्वक बनाया गया',
    labourerUpdatedSuccessfully: 'मजदूर सफलतापूर्वक अपडेट किया गया',
    labourerDeletedSuccessfully: 'मजदूर सफलतापूर्वक हटा दिया गया',
    paymentRecordedSuccessfully: 'भुगतान सफलतापूर्वक दर्ज किया गया',
    recordAddedSuccessfully: 'रिकॉर्ड सफलतापूर्वक जोड़ा गया',
    recordUpdatedSuccessfully: 'रिकॉर्ड सफलतापूर्वक अपडेट किया गया',
    labourGroupDeletedSuccessfully: 'श्रम समूह सफलतापूर्वक हटा दिया गया',
    borrowingAddedSuccessfully: 'उधार सफलतापूर्वक जोड़ा गया!',
    borrowingDeletedSuccessfully: 'उधार सफलतापूर्वक हटा दिया गया',
    borrowingUpdatedSuccessfully: 'उधार सफलतापूर्वक अपडेट किया गया!',
    borrowingReturnedSuccessfully: 'उधार को लौटाए गए के रूप में चिह्नित किया गया और मनी रिकॉर्ड सफलतापूर्वक बनाया गया!',
    exportPDF: 'PDF निर्यात करें',
    searchLotNumbers: 'लॉट नंबर खोजें',
    allFields: 'सभी खेत',
    hideForm: 'फॉर्म छिपाएं',
    enterLotNumber: 'लॉट नंबर दर्ज करें',
    enterFieldName: 'खेत का नाम दर्ज करें',
    enterSmallPackets: 'छोटे पैकेट दर्ज करें',
    enterMediumPackets: 'मध्यम पैकेट दर्ज करें',
    enterLargePackets: 'बड़े पैकेट दर्ज करें',
    enterXlargePackets: 'अतिरिक्त बड़े पैकेट दर्ज करें',
    enterNotes: 'नोट्स दर्ज करें',
    pleaseFillAllRequiredFields: 'कृपया सभी आवश्यक फ़ील्ड भरें',
    pleaseEnterValidPacketCount: 'कृपया मान्य पैकेट संख्या दर्ज करें',
    failedToCreateLotNumber: 'लॉट नंबर बनाने में विफल',
    failedToUpdateLotNumber: 'लॉट नंबर अपडेट करने में विफल',
    confirmDeleteLotNumber: 'क्या आप वाकई इस लॉट नंबर को हटाना चाहते हैं?',
    failedToDeleteLotNumber: 'लॉट नंबर हटाने में विफल',
    getStartedByAddingLotNumber: 'अपना पहला लॉट नंबर जोड़कर शुरुआत करें',
    storedOn: 'भंडारित किया गया',
    saving: 'सहेजा जा रहा है...',
    adding: 'जोड़ा जा रहा है...',
    updating: 'अपडेट किया जा रहा है...',
    enterAreaInAcres: 'एकड़ में क्षेत्रफल दर्ज करें',
    enterYear: 'वर्ष दर्ज करें',
    noFieldsYet: 'अभी तक कोई खेत नहीं',
    getStartedByAddingFirstField: 'अपना पहला खेत जोड़कर शुरुआत करें',
    addYourFirstField: 'अपना पहला खेत जोड़ें',
    updateField: 'खेत अपडेट करें',
    yieldRecords: 'उपज रिकॉर्ड',
    addYield: 'उपज जोड़ें',
    noYieldRecordsYet: 'अभी तक कोई उपज रिकॉर्ड नहीं',
    acres: 'एकड़',
    area: 'क्षेत्रफल',
    updateYield: 'उपज अपडेट करें',

    // Dashboard Statistics
    totalExpenses: 'कुल खर्च',
    totalRecords: 'कुल रिकॉर्ड',
    thisMonth: 'इस महीने',
    exportPdf: 'PDF निर्यात करें',
    totalReturned: 'कुल लौटाया गया',
    pendingReturns: 'लंबित वापसी',
    totalLots: 'कुल लॉट',
    searchLotNumber: 'लॉट नंबर खोजें',
    labourEarnings: 'श्रमिक कमाई',

    // Dashboard
    farmDashboard: 'फार्म डैशबोर्ड',
    overview: 'आपके फार्म के प्रदर्शन और मेट्रिक्स का अवलोकन',
    totalFields: 'कुल खेत',
    totalYield: 'कुल उपज',
    laborCost: 'श्रम लागत',
    expenses: 'खर्च',
    yieldByPotatoType: 'आलू के प्रकार के अनुसार उपज',
    yieldDistributionSubtitle: 'विभिन्न आलू श्रेणियों में उपज का वितरण',
    sevenDayForecast: '7-दिन का पूर्वानुमान',
    weatherAlert: 'मौसम सतर्कता',
    large: 'बड़ा',
    medium: 'मध्यम',
    small: 'छोटा',
    overlarge: 'अतिबड़ा',
    packets: 'पैकेट',
    totalYieldLabel: 'कुल उपज',
    totalTransported: 'कुल परिवहन',
    weatherForecast: '7-दिन का मौसम पूर्वानुमान',
    currentWeather: 'वर्तमान मौसम',
    gettingLocation: 'लोकेशन प्राप्त हो रही है...',
    locationAccessDenied: 'लोकेशन एक्सेस अस्वीकृत',
    locationNotSupported: 'लोकेशन समर्थित नहीं',
    locationError: 'लोकेशन त्रुटि हुई',
    retry: 'पुनः प्रयास करें',
    locationAccessRequired: 'मौसम डेटा के लिए लोकेशन एक्सेस आवश्यक है',
    enableLocationAccess: 'अपने ब्राउज़र सेटिंग्स में लोकेशन एक्सेस सक्षम करें',
    tryAgain: 'पुनः प्रयास करें',
    failedToGetWeatherData: 'मौसम डेटा प्राप्त करने में विफल',
    geolocationNotSupported: 'आपके ब्राउज़र में जियोलोकेशन समर्थित नहीं है',
    fetchingWeatherData: 'मौसम डेटा प्राप्त हो रहा है...',
    weatherDataFetched: 'जेमिनी एपीआई का उपयोग करके मौसम डेटा प्राप्त किया गया',

    // Fields
    selectField: 'खेत चुनें',
    fieldsManagement: 'खेत प्रबंधन',
    addNewField: 'नया खेत जोड़ें',
    fieldName: 'खेत का नाम',
    location: 'स्थान',
    size: 'आकार (एकड़)',
    potatoType: 'आलू का प्रकार',
    plantingDate: 'रोपण तिथि',
    expectedHarvestDate: 'अपेक्षित कटाई तिथि',
    addField: 'खेत जोड़ें',
    editField: 'खेत संपादित करें',
    deleteField: 'खेत हटाएं',
    saveChanges: 'परिवर्तन सहेजें',
    cancel: 'रद्द करें',

    // Money Records
    addNewBorrowing: 'नया उधार जोड़ें',
    paidTo: 'किसे भुगतान किया',
    enterRecipientName: 'प्राप्तकर्ता का नाम दर्ज करें',
    enterAmount: 'राशि दर्ज करें',
    selectPaymentMethod: 'भुगतान विधि चुनें',
    enterAnyAdditionalNotes: 'कोई अतिरिक्त नोट्स दर्ज करें',
    paymentMethod: 'भुगतान विधि',
    records: 'रिकॉर्ड',
    searchByPaidToAmountMethodNotesDate: 'भुगतानकर्ता, राशि, विधि, नोट्स या तिथि से खोजें',
    moneyRecords: 'मनी रिकॉर्ड',
    addNewRecord: 'नया रिकॉर्ड जोड़ें',
    recordType: 'रिकॉर्ड प्रकार',
    amount: 'राशि',
    description: 'विवरण',
    date: 'तिथि',
    income: 'आय',
    expense: 'खर्च',
    addRecord: 'रिकॉर्ड जोड़ें',
    editRecord: 'रिकॉर्ड संपादित करें',
    deleteRecord: 'रिकॉर्ड हटाएं',
    updateRecord: 'रिकॉर्ड अपडेट करें',
    totalIncome: 'कुल आय',
    totalExpense: 'कुल खर्च',
    netBalance: 'शुद्ध शेष',
    noRecordsFound: 'कोई रिकॉर्ड नहीं मिला',
    addYourFirstRecord: 'अपना पहला रिकॉर्ड जोड़ें',

    // Borrowings
    borrowingsManagement: 'उधार प्रबंधन',
    addNewBorrowing: 'नया उधार जोड़ें',
    lenderName: 'लेंडर का नाम',
    borrowingAmount: 'उधार राशि',
    interestRate: 'ब्याज दर',
    borrowingDate: 'उधार तिथि',
    repaymentDate: 'चुकौती तिथि',
    status: 'स्थिति',
    pending: 'लंबित',
    completed: 'पूर्ण',
    overdue: 'अतिदेय',
    addBorrowing: 'उधार जोड़ें',
    editBorrowing: 'उधार संपादित करें',
    deleteBorrowing: 'उधार हटाएं',
    updateBorrowing: 'उधार अपडेट करें',
    totalBorrowed: 'कुल उधार लिया गया',
    totalRepaid: 'कुल चुकौती की गई',
    outstandingAmount: 'बकाया राशि',
    noBorrowingsFound: 'कोई उधार नहीं मिला',
    addYourFirstBorrowing: 'अपना पहला उधार जोड़ें',
    borrowingRecordNotFound: 'उधार रिकॉर्ड नहीं मिला',
    alreadyReturned: 'यह उधार पहले ही वापस कर दिया गया है',

    // New Borrowing Keys (Hindi)
    borrowerName: 'उधारकर्ता का नाम',
    enterBorrowerName: 'उधारकर्ता का नाम दर्ज करें',
    borrowDate: 'उधार की तारीख',
    expectedReturnDate: 'अपेक्षित वापसी तिथि',
    actualReturn: 'वास्तविक वापसी',
    actualReturnDate: 'वास्तविक वापसी तिथि',
    returned: 'लौटाया गया',
    noBorrowingRecordsYet: 'अभी तक कोई उधार रिकॉर्ड नहीं',
    startTrackingBorrowings: 'अपना पहला रिकॉर्ड जोड़कर अपनी उधारी को ट्रैक करना शुरू करें',
    borrowedOn: 'को उधार लिया',
    expectedReturn: 'अपेक्षित वापसी',
    returnedOn: 'को वापस किया गया',
    markReturned: 'वापस चिह्नित करें',
    pleaseFillRequiredFieldsBorrowing: 'कृपया सभी आवश्यक फ़ील्ड भरें: उधारकर्ता का नाम, राशि और उधार की तारीख',
    enterValidAmount: 'कृपया 0 से अधिक मान्य राशि दर्ज करें',
    failedToCreateBorrowing: 'उधार रिकॉर्ड बनाने में विफल। कृपया पुन: प्रयास करें।',
    failedToUpdateBorrowing: 'उधार रिकॉर्ड अपडेट करने में विफल। कृपया पुन: प्रयास करें।',
    failedToDeleteBorrowing: 'उधार रिकॉर्ड हटाने में विफल। कृपया पुन: प्रयास करें।',
    failedToReturnBorrowing: 'उधार स्थिति अपडेट करने और मनी रिकॉर्ड बनाने में विफल। कृपया पुन: प्रयास करें।',

    // Lot Numbers
    lotNumbersManagement: 'लॉट नंबर प्रबंधन',
    lotNumberManagement: 'लॉट नंबर प्रबंधन', // Alternate key for compatibility
    addNewLotNumber: 'नया लॉट नंबर जोड़ें',
    lotNumberName: 'लॉट नंबर',
    category: 'श्रेणी',
    packetCount: 'पैकेट गणना',
    storageDate: 'भंडारण तिथि',
    notes: 'टिप्पणियाँ',
    addLotNumber: 'लॉट नंबर जोड़ें',
    editLotNumber: 'लॉट नंबर संपादित करें',
    deleteLotNumber: 'लॉट नंबर हटाएं',
    updateLotNumber: 'लॉट नंबर अपडेट करें',
    totalPackets: 'कुल पैकेट',
    noLotNumbersFound: 'कोई लॉट नंबर नहीं मिला',
    addYourFirstLotNumber: 'अपना पहला लॉट नंबर जोड़ें',

    // Labour Management
    labourManagement: 'श्रम प्रबंधन',
    manageLabourGroups: 'श्रम समूह प्रबंधन',
    manageLabourers: 'मजदूर प्रबंधन',
    labourGroups: 'श्रम समूह',
    addGroup: 'समूह जोड़ें',
    groupName: 'समूह का नाम',
    enterGroupName: 'समूह का नाम दर्ज करें',
    createGroup: 'समूह बनाएं',
    updateGroup: 'समूह अपडेट करें',
    deleteGroup: 'समूह हटाएं',
    labourers: 'मजदूर',
    addLabourer: 'मजदूर जोड़ें',
    name: 'नाम',
    village: 'गांव',
    dailyWage: 'दैनिक मजदूरी',
    phoneNumber: 'फोन नंबर',
    enterName: 'नाम दर्ज करें',
    enterVillage: 'गांव दर्ज करें',
    enterDailyWage: 'दैनिक मजदूरी दर्ज करें',
    enterPhoneNumber: 'फोन नंबर दर्ज करें',
    selectGroup: 'समूह चुनें',
    createLabourer: 'मजदूर बनाएं',
    updateLabourer: 'मजदूर अपडेट करें',
    deleteLabourer: 'मजदूर हटाएं',
    payments: 'भुगतान',
    addPayment: 'भुगतान जोड़ें',
    paymentDate: 'भुगतान तिथि',
    workingDays: 'कार्य दिवस',
    paymentType: 'भुगतान प्रकार',
    daily: 'दैनिक',
    weekly: 'साप्ताहिक',
    monthly: 'मासिक',
    createPayment: 'भुगतान बनाएं',
    updatePayment: 'भुगतान अपडेट करें',
    deletePayment: 'भुगतान हटाएं',
    totalPayments: 'कुल भुगतान',
    noPaymentsFound: 'कोई भुगतान नहीं मिला',
    addYourFirstPayment: 'अपना पहला भुगतान जोड़ें',
    financialSummary: 'वित्तीय सारांश',
    exportAsPDF: 'PDF के रूप में निर्यात करें',
    generatedOn: 'उत्पन्न किया गया',
    summaryStatistics: 'सारांश आँकड़े',
    labourersDetails: 'मजदूर विवरण',
    recentPaymentHistory: 'हाल के भुगतान इतिहास',
    noteAttendanceSystem: 'नोट: कार्य दिवस उपस्थिति प्रणाली लागू होने पर अपडेट किए जाएंगे।',
    attendance: 'उपस्थिति',
    attendanceDate: 'उपस्थिति तिथि',
    attendanceStatus: 'उपस्थिति स्थिति',
    fullDay: 'पूरा दिन',
    halfDay: 'आधा दिन',
    absent: 'अनुपस्थित',
    saveAttendance: 'उपस्थिति सहेजें',
    attendanceTodayOnly: 'उपस्थिति केवल आज के लिए दर्ज की जा सकती है।',
    exportAttendancePDF: 'उपस्थिति PDF निर्यात करें',
    failedToFetchAttendance: 'उपस्थिति लाने में विफल',
    failedToSaveAttendance: 'उपस्थिति सहेजने में विफल',
    attendanceMarkedSuccessfully: 'उपस्थिति सफलतापूर्वक दर्ज की गई',
    joiningDate: 'शामिल होने की तिथि',
    totalWages: 'कुल मजदूरी',
    amountToPay: 'भुगतान की जाने वाली राशि',
    overpaidAmount: 'अधिक भुगतान की गई राशि',
    weatherAlertPrefix: 'सावधान: इन दिनों बारिश की संभावना है -',
    takePrecautions: 'कृपया आवश्यक सावधानी बरतें।',
    weatherClearForecast: 'अगले 7 दिनों तक मौसम साफ रहने की उम्मीद है। खेतो में काम करने के लिए स्थितियां अच्छी हैं।',

    // Additional translation keys
    actions: 'कार्रवाई',
    field: 'खेत',
    enterLargePotatoes: 'बड़े आलू दर्ज करें',
    enterMediumPotatoes: 'मध्यम आलू दर्ज करें',
    enterSmallPotatoes: 'छोटे आलू दर्ज करें',
    enterOverlargePotatoes: 'अतिबड़े आलू दर्ज करें',
    optionalNotes: 'वैकल्पिक नोट्स',

    // Daily Report (Hindi)
    dailyReport: 'दैनिक रिपोर्ट (PDF)',
    downloadDailyReport: 'आज की गतिविधि रिपोर्ट डाउनलोड करें',
    noActivitiesToday: 'आज के लिए कोई नई गतिविधि दर्ज नहीं की गई।',
    reportGeneratedAt: 'तैयार किया गया समय',
    reportDate: 'रिपोर्ट की तारीख'
  }
};

// Language Provider Component
export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'hi' : 'en');
  };

  // Memoize the translation function for performance
  const t = React.useCallback((key) => {
    return translations[language][key] || key;
  }, [language]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = React.useMemo(() => ({
    language,
    toggleLanguage,
    t
  }), [language, t]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Export just the language provider
export default LanguageProvider;
